import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import {
  RowenaAvailableVariable,
  RowenaAvailableVariableGroup,
} from 'types/api/SystemTenant/RowenaAI/RowenaContentTypes';

interface VariableTreeProps {
  groups: RowenaAvailableVariableGroup[];
  onInsertVariable: (variable: RowenaAvailableVariable) => void;
  disabled?: boolean;
  isLoading?: boolean;
  pageTypeSelected?: boolean;
}

function collectGroupIds(groups: RowenaAvailableVariableGroup[]): string[] {
  const ids: string[] = [];
  const walk = (gs: RowenaAvailableVariableGroup[]) => {
    gs.forEach((g) => {
      ids.push(g.id);
      if (g.groups?.length) walk(g.groups);
    });
  };
  walk(groups);
  return ids;
}

export default function VariableTree({
  groups,
  onInsertVariable,
  disabled,
  isLoading,
  pageTypeSelected,
}: VariableTreeProps) {
  const renderVariableLeaf = (variable: RowenaAvailableVariable) => (
    <TreeItem
      key={variable.name}
      nodeId={variable.name}
      label={
        <Stack
          direction='row'
          alignItems='center'
          gap={1}
          sx={{
            py: 0.25,
            '&:hover .insert-button': { opacity: 1, pointerEvents: 'auto' },
          }}
        >
          <Tooltip title={variable.description} arrow placement='right'>
            <Box
              component='span'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontFamily: 'monospace',
                fontSize: 13,
                color: disabled ? '#98A2AE' : '#3B4797',
              }}
            >
              <DataObjectOutlinedIcon fontSize='small' />@{variable.name}
            </Box>
          </Tooltip>
          <Button
            className='insert-button'
            size='small'
            variant='outlined'
            startIcon={<AddIcon />}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) onInsertVariable(variable);
            }}
            sx={{
              opacity: 0,
              pointerEvents: 'none',
              transition: 'opacity 0.15s',
              textTransform: 'none',
              py: 0,
              px: 1,
              minWidth: 'auto',
              fontSize: 11,
              lineHeight: 1.5,
            }}
          >
            Insert
          </Button>
        </Stack>
      }
    />
  );

  const renderGroup = (group: RowenaAvailableVariableGroup, depth: number) => (
    <TreeItem
      key={group.id}
      nodeId={group.id}
      label={
        <Stack direction='row' alignItems='center' gap={0.5} sx={{ py: 0.25 }}>
          <FolderOpenOutlinedIcon fontSize='small' sx={{ color: '#3B4797' }} />
          <Typography variant='body2' fontWeight={depth === 0 ? 600 : 500}>
            {group.label}
          </Typography>
        </Stack>
      }
    >
      {group.variables.map(renderVariableLeaf)}
      {(group.groups ?? []).map((child) => renderGroup(child, depth + 1))}
    </TreeItem>
  );

  const renderBody = () => {
    if (!pageTypeSelected) {
      return (
        <Typography variant='input-label-gray' fontSize={12} sx={{ mt: 1, fontStyle: 'italic' }}>
          Select a page type to see the variables available for that page.
        </Typography>
      );
    }
    if (isLoading) {
      return (
        <Box sx={{ mt: 1, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator />
        </Box>
      );
    }
    if (groups.length === 0) {
      return (
        <Typography variant='input-label-gray' fontSize={12} sx={{ mt: 1, fontStyle: 'italic' }}>
          No variables are available for the selected page type.
        </Typography>
      );
    }
    return (
      <TreeView
        aria-label='Available Rowena variables'
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
        defaultExpanded={['variables-and-context', ...collectGroupIds(groups)]}
        sx={{
          mt: 1,
          '& .MuiTreeItem-content': {
            py: 0.25,
          },
        }}
      >
        <TreeItem
          nodeId='variables-and-context'
          label={
            <Stack direction='row' alignItems='center' gap={0.5} sx={{ py: 0.25 }}>
              <FolderOpenOutlinedIcon fontSize='small' sx={{ color: '#3B4797' }} />
              <Typography variant='body2' fontWeight={700}>
                Variables and Context
              </Typography>
            </Stack>
          }
        >
          {groups.map((group) => renderGroup(group, 0))}
        </TreeItem>
      </TreeView>
    );
  };

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        border: '1px dashed #E2E8F0',
        borderRadius: 1,
        maxWidth: '80%',
      }}
    >
      <Stack direction='column'>
        <Typography variant='input-label' fontWeight={700} fontSize={13}>
          Available variables
        </Typography>
        <Typography variant='input-label-gray' fontSize={12}>
          Hover a variable and click <strong>Insert</strong> to add it at the cursor, or type <strong>@</strong> in the
          editor to search. Variables are replaced with real values when the content is shown.
        </Typography>
      </Stack>
      {renderBody()}
    </Box>
  );
}
